import {type FromSchema} from 'json-schema-to-ts'

export const listingsFeedProcessingReportSchemaV2 = {
  $comment:
    'JSON Schema describing the document format for JSON Selling Partner Listings Feeds Processing Report (v2).',
  title: 'Selling Partner JSON Listings Feed Processing Report (v2)',
  properties: {
    header: {
      title: 'Processing Report Header',
      description: 'Header information about the feed submission.',
      properties: {
        sellerId: {
          title: 'Selling Partner Identifier',
          description:
            'Identifier for the Selling Partner, such as the Merchant Account or Vendor Code.',
          type: 'string',
        },
        version: {
          title: 'Feed Version',
          description:
            'Version of the JSON Selling Partner Listings Feeds specification used for the feed submission.',
          const: '2.0',
          type: 'string',
        },
        feedId: {
          title: 'Feed Identifier',
          description:
            'The identifier for the feed. This identifier is unique only in combination with a seller ID.',
          type: 'string',
        },
        report: {
          title: 'Report',
          description: 'The report information for the feed submission.',
          properties: {
            includedData: {
              title: 'Included Data',
              description:
                'When "includedData" elements are in the feed submission, the processing report that generates for the feed submission includes the listings items output from the Selling Partner Listings Items API with the "includedData" elements from the request. The report includes issues that prevent the acceptance of messages regardless of the "includedData" elements in the feed request.',
              type: 'array',
              minItems: 1,
              items: {
                type: 'string',
                enum: [
                  'summaries',
                  'attributes',
                  'issues',
                  'offers',
                  'fulfillmentAvailability',
                  'procurement',
                  'relationships',
                  'productTypes',
                ],
              },
            },
            apiVersion: {
              title: 'Selling Partner Listings Items API Version',
              description:
                'The version of the Selling Partner Listings Items API to use to retrieve the listing items.',
              type: 'string',
              enum: ['2021-08-01'],
            },
          },
          required: ['includedData', 'apiVersion'],
          type: 'object',
        },
      },
      required: ['sellerId', 'version', 'feedId'],
      type: 'object',
    },
    issues: {
      title: 'Issues Encountered Processing Messages',
      description: 'Issues containing message processing information for the Selling Partner.',
      minItems: 0,
      items: {
        title: 'Issue',
        description:
          'Issue containing message processing information corresponding to an individual listings data submission.',
        properties: {
          messageId: {
            title: 'Message Identifier',
            description:
              'Identifier for the message that is unique within this feed submission. Correlated to Identifiers provided in the submitted feed. Not provided if the error is not related to a message (e.g. feed formatting issue).',
            examples: [1],
            minimum: 1,
            type: 'integer',
          },
          sku: {
            title: 'SKU (Stock Keeping Unit)',
            description:
              'Selling Partner SKU (stock keeping unit) identifier for the listing. SKU uniquely identifies a listing for a Selling Partner.',
            examples: ['ABC123'],
            minLength: 1,
            type: 'string',
          },
          code: {
            title: 'Error Code',
            description: 'An Optional error code that maps to documentation.',
            examples: ['ABC123'],
            minLength: 1,
            type: 'string',
          },
          severity: {
            title: 'Error Severity',
            description: 'The severity of the issue.',
            enum: ['ERROR', 'WARNING', 'INFO'],
            type: 'string',
          },
          message: {
            title: 'Localized Error Message',
            description: 'Localized Error Message Associated with the issue encountered.',
            examples: ["'[batteries_required]' is required but not supplied."],
            minLength: 1,
            type: 'string',
          },
          attributeName: {
            description: 'Name of the attribute associated with the issue, if applicable.',
            examples: ['item_type_name'],
            type: 'string',
          },
        },
        required: ['severity', 'message'],
        type: 'object',
      },
      type: 'array',
    },
    summary: {
      title: 'Processing Report Summary',
      description:
        'Aggregate counts of errors, warnings, and statuses related to the acceptance of feed messages. When requested, Item Summary includes aggregate counts of items, errors, and warnings related to the listing items created or updated by this feed submission.',
      properties: {
        errors: {
          title: 'Errors',
          description: 'Number of errors encountered processing the feed.',
          examples: [1],
          minimum: 0,
          type: 'integer',
        },
        warnings: {
          title: 'Warnings',
          description: 'Number of warnings encountered processing the feed.',
          examples: [1],
          minimum: 0,
          type: 'integer',
        },
        messagesProcessed: {
          title: 'Messages Processed',
          description: 'Number of messages processed.',
          examples: [1],
          minimum: 0,
          type: 'integer',
        },
        messagesAccepted: {
          title: 'Messages Accepted',
          description:
            'Number of messages that were accepted, the associated updates are being applied.',
          examples: [1],
          minimum: 0,
          type: 'integer',
        },
        messagesInvalid: {
          title: 'Messages Invalid',
          description:
            'Number of messages that were invalid, the associated updates will not be applied.',
          examples: [1],
          minimum: 0,
          type: 'integer',
        },
        itemSummary: {
          title: 'Item Summary',
          description:
            'Aggregate counts of items, errors, and warnings related to the listing items created or updated by this feed submission and included in the Items section of this report.',
          type: 'object',
          properties: {
            items: {
              title: 'Items',
              description:
                'Number of listing items created or updated by this feed submission and included in the Items section of this report.',
              examples: [1],
              minimum: 0,
              type: 'integer',
            },
            errors: {
              title: 'Errors',
              description:
                'Number of errors across all listing items created or updated by this feed submission.',
              examples: [1],
              minimum: 0,
              type: 'integer',
            },
            warnings: {
              title: 'Warnings',
              description:
                'Number of warnings across all listing items created or updated by this feed submission.',
              examples: [1],
              minimum: 0,
              type: 'integer',
            },
          },
          required: ['items', 'errors', 'warnings'],
        },
      },
      required: ['errors', 'warnings', 'messagesProcessed', 'messagesAccepted', 'messagesInvalid'],
      type: 'object',
    },
    items: {
      title: 'Listings Items',
      description:
        'A set of listings items and their specific data sets that originates from the feed request’s "includedData" header parameter.',
      type: 'array',
      items: {
        title: 'Item',
        description: 'A listings item.',
        additionalProperties: true,
        minProperties: 1,
        type: 'object',
      },
    },
  },
  required: ['header', 'issues', 'summary'],
  type: 'object',
} as const

export type ListingsFeedProcessingReportSchemaV2 = FromSchema<
  typeof listingsFeedProcessingReportSchemaV2
>
